import { queryOptions, skipToken } from "@tanstack/react-query";
import { UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER } from "~/constants/apiEndpoints";
import { CALL_TYPE } from "~/constants/call";
import type {
  AgeKey,
  AreaKey,
  BustSizeKey,
  SearchFilters,
  SearchUserListResponse,
} from "~/features/search/types";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";

/** 検索 API の 1 回の取得件数（これと同数返ったら、まだ先がある） */
export const SEARCH_LIMIT = 200;

const AGE_MAP: Record<AgeKey, { lower: number; upper: number }> = {
  noPreference: { lower: 18, upper: 100 },
  under20: { lower: 18, upper: 20 },
  "21to25": { lower: 21, upper: 25 },
  "26to30": { lower: 26, upper: 30 },
  "31to35": { lower: 31, upper: 35 },
  "36to40": { lower: 36, upper: 40 },
  "41to45": { lower: 41, upper: 45 },
  over46: { lower: 46, upper: 100 },
};

/** エリア → 県番号。番号は REGION_LIST（BE の parseRegions と同じ並び）の index */
const AREA_MAP: Record<AreaKey, number[] | undefined> = {
  noPreference: undefined,
  hokkaido: [1],
  tohoku: [2, 3, 4, 5, 6, 7],
  kanto: [8, 9, 10, 11, 12, 13, 14],
  chubu: [15, 16, 17, 18, 19, 20, 21, 22, 23],
  kansai: [24, 25, 26, 27, 28, 29, 30],
  chugoku: [31, 32, 33, 34, 35],
  shikoku: [36, 37, 38, 39],
  kyushuOkinawa: [40, 41, 42, 43, 44, 45, 46, 47],
};

const BUST_SIZE_LABEL: Record<
  Exclude<BustSizeKey, "noPreference" | "bigBreasts">,
  string
> = {
  aCup: "Aカップ",
  bCup: "Bカップ",
  cCup: "Cカップ",
  dCup: "Dカップ",
  eCup: "Eカップ",
};

/** SearchFilters（camelCase）を BE 用の snake_case パラメータに変換する */
export const buildSearchRequestBody = (
  filters: SearchFilters,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    limit: SEARCH_LIMIT,
    sort_type: 0,
    default_avatar_flag: false,
  };

  if (filters.isNew) body.is_new = true;

  if (filters.callType === CALL_TYPE.VIDEO) body.video_call_waiting = true;
  if (filters.callType === CALL_TYPE.VOICE) body.voice_call_waiting = true;

  if (filters.recentVideoCall) body.recent_video_call = true;

  if (filters.hasLovense) body.has_lovense = true;

  if (filters.minReviewScore !== undefined) {
    body.min_review_score = filters.minReviewScore;
  }

  const age = AGE_MAP[filters.ageKey];
  body.lower_age = age.lower;
  body.upper_age = age.upper;

  const region = AREA_MAP[filters.areaKey];
  if (region !== undefined) body.region = region;

  if (filters.bustSizeKey === "bigBreasts") {
    body.is_big_breasts = true;
  } else if (filters.bustSizeKey !== "noPreference") {
    body.bust_size = BUST_SIZE_LABEL[filters.bustSizeKey];
  }

  if (filters.bodyTypes.length > 0) body.bdy_tpe = filters.bodyTypes;

  if (filters.maritalStatus === "unmarried") {
    body.marriage_history = [0, 1];
  } else if (filters.maritalStatus === "married") {
    body.marriage_history = [2, 3, 4];
  }

  if (filters.isFleaMarket) body.is_flea_market_on_sale = true;

  if (filters.nickname.trim() !== "") body.user_name = filters.nickname.trim();

  return body;
};

/** filters が null のときはリクエストしない（メッセージ一覧の絞り込み未適用時） */
export const searchUsersQueryOptions = (filters: SearchFilters | null) =>
  queryOptions({
    queryKey: queryKeys.search.list(filters),
    queryFn:
      filters === null
        ? skipToken
        : () =>
            apiClient<SearchUserListResponse>(
              UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
              buildSearchRequestBody(filters),
            ),
    staleTime: 0,
  });
